import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import Button from "../../../../components/Button";
import NoticeDialog from "../../../../components/v2/NoticeModal/NoticeDialog";
import Select, { MaterialSelectEvent } from "../../../../components/v2/Select";
import Snackbar from "../../../../components/v2/Snackbar";
import Table, { TableColumn } from "../../../../components/v2/Table";
import AdminNoticesService, {
  AdminNotice,
  NoticeStatus,
  noticeFailure,
} from "../../../../services/AdminNoticesService";
import ConfirmDialog, { ConfirmAction } from "./ConfirmDialog";
import { formatDateTime } from "./dates";
import NoticeFormDialog from "./NoticeFormDialog";
import StatusTag from "./StatusTag";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.notices";
const STATUSES: NoticeStatus[] = ["draft", "scheduled", "live", "ended"];

type Status = "loading" | "ready" | "error";
type StatusFilter = NoticeStatus | "all";

interface Confirmation {
  action: ConfirmAction;
  notice: AdminNotice;
}

export default function AdminNotices() {
  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const filter = useMemo(() => parseFilter(search), [search]);

  const [notices, setNotices] = useState<AdminNotice[]>([]);
  const [status, setStatus] = useState<Status>("loading");
  const [editing, setEditing] = useState<AdminNotice | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState<AdminNotice | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(() => {
    setStatus("loading");
    AdminNoticesService.list()
      .then((list) => {
        setNotices(Array.isArray(list) ? list : []);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closeMessage = useCallback(() => setMessage(null), []);

  const rows = filter === "all" ? notices : notices.filter((item) => item.status === filter);

  const handleSaved = (saved: AdminNotice, created: boolean) => {
    setCreating(false);
    setEditing(null);
    setMessage(t(`${keyI18n}.notices.${created ? "created" : "updated"}`, { title: saved.title }));
    load();
  };

  const duplicate = (item: AdminNotice) => {
    AdminNoticesService.duplicate(item.id)
      .then((copy) => {
        setMessage(t(`${keyI18n}.notices.duplicated`, { title: item.title }));
        setEditing(copy);
        load();
      })
      .catch(() => setMessage(t(`${keyI18n}.notices.error`)));
  };

  const confirmErrorMessage = (error: unknown) => {
    const failure = noticeFailure(error);
    if (failure && failure.conflict) {
      return t(`${keyI18n}.form.errors.base.overlap`, {
        title: failure.conflict.title,
        start: formatDateTime(failure.conflict.startsAt),
        end: formatDateTime(failure.conflict.endsAt),
      });
    }
    if (failure && failure.errors.endsAt && failure.errors.endsAt.includes("past")) {
      return t(`${keyI18n}.confirm.ended`);
    }
    return t(`${keyI18n}.confirm.error`);
  };

  const confirm = () => {
    if (!confirmation) {
      return;
    }

    const { action, notice: item } = confirmation;
    const request: Promise<unknown> = action === "publish"
      ? AdminNoticesService.publish(item.id)
      : action === "unpublish"
        ? AdminNoticesService.unpublish(item.id)
        : AdminNoticesService.remove(item.id);

    setConfirming(true);
    setConfirmError(null);
    request
      .then(() => {
        setConfirmation(null);
        setMessage(t(`${keyI18n}.notices.${action}`, { title: item.title }));
        load();
      })
      .catch((error: unknown) => setConfirmError(confirmErrorMessage(error)))
      .finally(() => setConfirming(false));
  };

  const openConfirmation = (action: ConfirmAction, item: AdminNotice) => {
    setConfirmError(null);
    setConfirmation({ action, notice: item });
  };

  const renderPeriod = (item: AdminNotice) => (
    <span className={style.period}>
      <span>{formatDateTime(item.startsAt)}</span>
      <span className={style.muted}>{t(`${keyI18n}.until`, { date: formatDateTime(item.endsAt) })}</span>
    </span>
  );

  const renderActions = (item: AdminNotice) => {
    const published = item.status !== "draft";

    return (
      <span className={style.actions}>
        <Button
          iconButton
          variant="text"
          size="small"
          aria-label={t(`${keyI18n}.actions.previewNotice`, { title: item.title })}
          tooltip={t(`${keyI18n}.actions.preview`)}
          tooltipPlacement="top-end"
          onClick={() => setPreviewing(item)}
          icon={<span className="material-symbols-sharp">visibility</span>}
        />
        <Button
          iconButton
          variant="text"
          size="small"
          aria-label={t(`${keyI18n}.actions.editNotice`, { title: item.title })}
          tooltip={t(`${keyI18n}.actions.edit`)}
          tooltipPlacement="top-end"
          onClick={() => setEditing(item)}
          icon={<span className="material-symbols-sharp">edit</span>}
        />
        <Button
          iconButton
          variant="text"
          size="small"
          aria-label={t(`${keyI18n}.actions.${published ? "unpublishNotice" : "publishNotice"}`, { title: item.title })}
          tooltip={t(`${keyI18n}.actions.${published ? "unpublish" : "publish"}`)}
          tooltipPlacement="top-end"
          onClick={() => openConfirmation(published ? "unpublish" : "publish", item)}
          icon={<span className="material-symbols-sharp">{published ? "unpublished" : "publish"}</span>}
        />
        <Button
          iconButton
          variant="text"
          size="small"
          aria-label={t(`${keyI18n}.actions.duplicateNotice`, { title: item.title })}
          tooltip={t(`${keyI18n}.actions.duplicate`)}
          tooltipPlacement="top-end"
          onClick={() => duplicate(item)}
          icon={<span className="material-symbols-sharp">content_copy</span>}
        />
        <Button
          iconButton
          variant="text"
          size="small"
          aria-label={published ? t(`${keyI18n}.actions.removePublished`) : t(`${keyI18n}.actions.removeNotice`, { title: item.title })}
          tooltip={published ? t(`${keyI18n}.actions.removePublished`) : t(`${keyI18n}.actions.remove`)}
          tooltipPlacement="top-end"
          softDisabled={published}
          onClick={published ? undefined : () => openConfirmation("remove", item)}
          icon={<span className="material-symbols-sharp">delete</span>}
        />
      </span>
    );
  };

  const columns: TableColumn<AdminNotice>[] = [
    { key: "title", header: t(`${keyI18n}.columns.title`), render: (item) => <span className={style.noticeTitle}>{item.title}</span> },
    { key: "period", header: t(`${keyI18n}.columns.period`), render: renderPeriod, width: "200px" },
    { key: "status", header: t(`${keyI18n}.columns.status`), render: (item) => <StatusTag status={item.status} />, width: "140px" },
    { key: "actions", header: t(`${keyI18n}.columns.actions`), render: renderActions, align: "end", width: "216px", srOnlyHeader: true },
  ];

  const renderCompactRow = (item: AdminNotice) => (
    <div className={style.compactRow}>
      <div className={style.compactMain}>
        <span className={`pgm-title-medium ${style.compactTitle}`}>{item.title}</span>
        <span className={`pgm-body-medium ${style.muted}`}>
          {t(`${keyI18n}.periodRange`, { start: formatDateTime(item.startsAt), end: formatDateTime(item.endsAt) })}
        </span>
        <span className={style.compactTags}>
          <StatusTag status={item.status} />
        </span>
      </div>
      {renderActions(item)}
    </div>
  );

  const emptyState = (
    <div className={style.empty}>
      <span aria-hidden="true" className={`material-symbols-sharp ${style.emptyIcon}`}>campaign</span>
      <p className={`pgm-title-medium ${style.emptyText}`}>{t(`${keyI18n}.empty.${filter}`)}</p>
    </div>
  );

  const filterOptions = (["all", ...STATUSES] as StatusFilter[])
    .map((value) => ({ value, label: t(`${keyI18n}.filter.${value}`) }));

  return (
    <div className={style.layout}>
      <header className={style.header}>
        <div className={style.heading}>
          <h1 className={`pgm-headline-small ${style.title}`}>{t(`${keyI18n}.title`)}</h1>
          <p className={`pgm-body-medium ${style.subtitle}`}>{t(`${keyI18n}.subtitle`)}</p>
        </div>
        <Button
          variant="filled"
          size="small"
          className={style.createButton}
          onClick={() => setCreating(true)}
          icon={<span className="material-symbols-sharp">add</span>}
        >
          {t(`${keyI18n}.actions.create`)}
        </Button>
      </header>

      <div className={style.filters}>
        <Select
          variant="outlined"
          className={style.statusFilter}
          label={t(`${keyI18n}.filter.label`)}
          value={filter}
          options={filterOptions}
          onChange={(event: MaterialSelectEvent) => history.replace({ search: serializeFilter(event.currentTarget.value as StatusFilter) })}
        />
      </div>

      {status === "error" ? (
        <div className={style.error} role="alert">
          <span aria-hidden="true" className={`material-symbols-sharp ${style.emptyIcon}`}>error</span>
          <p className={`pgm-title-medium ${style.emptyText}`}>{t(`${keyI18n}.loadError`)}</p>
          <Button variant="outlined" size="small" onClick={load}>
            {t(`${keyI18n}.retry`)}
          </Button>
        </div>
      ) : (
        <Table
          caption={t(`${keyI18n}.caption`)}
          columns={columns}
          rows={rows}
          rowKey={(item) => item.id}
          loading={status === "loading"}
          loadingRows={4}
          emptyState={emptyState}
          renderCompactRow={renderCompactRow}
        />
      )}

      <NoticeFormDialog
        open={creating || editing !== null}
        notice={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />

      {previewing && (
        <NoticeDialog
          open
          notice={{
            title: previewing.title,
            text: previewing.body,
            confirmation_button: previewing.confirmationLabel,
            action: previewing.actionLabel && previewing.actionUrl
              ? { label: previewing.actionLabel, url: previewing.actionUrl }
              : null,
          }}
          onClose={() => setPreviewing(null)}
        />
      )}

      <ConfirmDialog
        action={confirmation ? confirmation.action : null}
        title={confirmation ? confirmation.notice.title : ""}
        busy={confirming}
        error={confirmError}
        onCancel={() => setConfirmation(null)}
        onConfirm={confirm}
      />

      <Snackbar message={message} onClose={closeMessage} />
    </div>
  );
}

function parseFilter(search: string): StatusFilter {
  const value = new URLSearchParams(search).get("status") as NoticeStatus | null;
  return value && STATUSES.includes(value) ? value : "all";
}

function serializeFilter(filter: StatusFilter): string {
  return filter === "all" ? "" : `?status=${filter}`;
}
